/**
 * creature/species.ts — The Shape of Every Living Thing
 *
 * A SpeciesTemplate is pure data: the skeleton, the proportions, the colors,
 * the gaits, the behavioral defaults. No methods, no inheritance, no classes.
 * Every cat in the world shares the same template; what makes each one unique
 * is its individual genetics, its accumulated behavior, its bond memories.
 *
 * This is the furniture.ts pattern applied to creatures: data defines the type,
 * the renderer switches on it, the systems read from it.
 */

import type { SkeletonDef } from "./skeleton.js";

// ─── Body Plan ──────────────────────────────────────────────────────────────

export type BodyPlan =
  | "quadruped"         // cat, dog, horse, wolf
  | "biped"             // chicken, penguin
  | "winged_quadruped"  // dragon, griffin
  | "serpentine";       // snake, worm

export type CreatureSize = "tiny" | "small" | "medium" | "large" | "huge";

// ─── Gait Pattern ───────────────────────────────────────────────────────────

/**
 * A gait defines how legs move at a given speed range.
 * Quadrupeds have multiple gaits: walk, trot, canter, gallop.
 * Each gait has its own leg phase offsets that create the characteristic
 * rhythm — walk is 4-beat, trot is 2-beat diagonal, gallop is asymmetric.
 */
export interface GaitPattern {
  /** Display name: "walk", "trot", "canter", "gallop" */
  name: string;
  /** Speed range in tiles/sec where this gait activates [min, max] */
  speedRange: [number, number];
  /**
   * Leg phase offsets keyed by leg tag.
   * Diagonal gait example: { leg_front_l: 0, leg_back_r: 0, leg_front_r: PI, leg_back_l: PI }
   */
  legPhaseOffsets: Record<string, number>;
  /** World units traveled per full walk cycle */
  strideLength: number;
  /** Vertical body oscillation amplitude (world units) */
  bodyBob: number;
  /** Lateral body sway amplitude (world units) */
  bodyRoll: number;
  /** Head bob amplitude (additional to body bob) */
  headBob: number;
}

// ─── Ear Style ──────────────────────────────────────────────────────────────

export type EarStyle = "pointed" | "floppy" | "round" | "tufted" | "none";

// ─── Body Shape ─────────────────────────────────────────────────────────────

/**
 * Physical proportions of the species. All in world units.
 * The renderer uses these to scale ellipses, arcs, and lines.
 */
export interface BodyShape {
  /** Distance from hip to shoulder (spine length) */
  bodyLength: number;
  /** Body ellipse width at widest point */
  bodyWidth: number;
  /** Head circle radius */
  headRadius: number;
  /** Number of tail segments (0 = no tail) */
  tailSegments: number;
  /** Total tail length in world units */
  tailLength: number;
  /** Ear rendering style */
  earStyle: EarStyle;
  /** Ear size multiplier (1.0 = species default) */
  earSize: number;
  /** Leg thickness in world units */
  legThickness: number;
  /** Whether this species has a mane (horses, lions) */
  hasMane: boolean;
  /** Whether this species has wings */
  hasWings: boolean;
  /** Wingspan in world units (only if hasWings) */
  wingSpan?: number;
  /** Neck length multiplier (1.0 = standard, >1 for horses/giraffes) */
  neckLength: number;
  /** Snout length (0 = flat face like cat, 1 = long like horse) */
  snoutLength: number;
  /** Leg length multiplier (1.0 = standard) */
  legLength: number;
}

// ─── Palette ────────────────────────────────────────────────────────────────

/** Color slots for the species. Each is a hex color string. */
export interface CreaturePalette {
  /** Main body color */
  body: string;
  /** Secondary markings (stripes, spots, patches) */
  markings: string;
  /** Eye color (iris) */
  eyes: string;
  /** Nose/muzzle color */
  nose: string;
  /** Accent color (paw pads, ear tips, tail tip) */
  accent: string;
  /** Underbelly color */
  belly: string;
}

// ─── Sound Set ──────────────────────────────────────────────────────────────

/** Audio keys for the synth engine, grouped by context */
export interface CreatureSounds {
  idle: string[];
  happy: string[];
  hungry: string[];
  walk: string[];
  run: string[];
  /** Species-specific: meow, bark, neigh, howl, roar */
  special: string[];
}

// ─── Behavior Genome Defaults ───────────────────────────────────────────────

/**
 * Default behavioral traits for a species.
 * Individual creatures vary from these via genetics and experience.
 */
export interface BehaviorGenomeDefaults {
  energy: number;         // 0 = sleepy, 1 = hyperactive
  sociability: number;    // 0 = aloof, 1 = clingy
  curiosity: number;      // 0 = stays put, 1 = explores everything
  obedience: number;      // 0 = independent, 1 = follows commands
  playfulness: number;    // 0 = serious, 1 = always plays
  courage: number;        // 0 = skittish, 1 = bold
  agility: number;        // affects turn speed, jump distance
  stamina: number;        // how long before needing rest
  nocturnality: number;   // 0 = diurnal, 1 = nocturnal
  territoriality: number; // how defensive of home
  packInstinct: number;   // tendency to cluster with same-species
}

// ─── Species Template ───────────────────────────────────────────────────────

/**
 * The complete data definition of a creature species.
 * Created once, shared by all instances. No methods.
 */
export interface SpeciesTemplate {
  /** Unique ID: "cat_house", "dog_labrador", "horse_standard" */
  id: string;
  /** Display name: "House Cat", "Labrador", "Horse" */
  displayName: string;
  /** Body plan determines skeleton structure and renderer branch */
  bodyPlan: BodyPlan;
  /** The skeleton definition */
  skeleton: SkeletonDef;
  /** Creature size category */
  size: CreatureSize;
  /** Base movement speed in tiles/sec */
  baseSpeed: number;
  /** Height at shoulder in world units (for mount rider attachment) */
  heightAtShoulder: number;
  /** Available gaits ordered by speed */
  gaits: GaitPattern[];
  /** Turn rate in radians/sec */
  turnRate: number;
  /** Default color palette */
  palette: CreaturePalette;
  /** Physical proportions */
  bodyShape: BodyShape;
  /** Default behavioral genome */
  behaviorDefaults: BehaviorGenomeDefaults;
  /** Whether this creature can be ridden */
  rideable: boolean;
  /** Joint ID where the rider sits (typically "spine" or "shoulder") */
  riderAttachJoint: string;
  /** Vertical offset above attachment joint for rider position */
  riderOffsetZ: number;
  /** Whether this creature can wear a saddle */
  saddleable: boolean;
  /** Audio keys */
  sounds: CreatureSounds;
  /** Room mood contribution when this creature is present */
  moodContribution: {
    warmth: number;
    coziness: number;
    energy: number;
    nature: number;
  };
}

// ─── Species Registry ───────────────────────────────────────────────────────

const registry = new Map<string, SpeciesTemplate>();

/** Register a species template */
export function registerSpecies(template: SpeciesTemplate): void {
  registry.set(template.id, template);
}

/** Look up a species template by ID */
export function getSpecies(id: string): SpeciesTemplate | undefined {
  return registry.get(id);
}

/** Get all registered species */
export function getAllSpecies(): SpeciesTemplate[] {
  return Array.from(registry.values());
}

// ─── Breed Variant Helper ───────────────────────────────────────────────────

/**
 * Create a breed variant from a base species template.
 * Overrides specific fields while inheriting everything else.
 */
export function createBreed(
  base: SpeciesTemplate,
  overrides: Partial<SpeciesTemplate> & { id: string; displayName: string }
): SpeciesTemplate {
  return {
    ...base,
    ...overrides,
    palette: { ...base.palette, ...(overrides.palette ?? {}) },
    bodyShape: { ...base.bodyShape, ...(overrides.bodyShape ?? {}) },
    behaviorDefaults: { ...base.behaviorDefaults, ...(overrides.behaviorDefaults ?? {}) },
    sounds: { ...base.sounds, ...(overrides.sounds ?? {}) },
    moodContribution: { ...base.moodContribution, ...(overrides.moodContribution ?? {}) },
  };
}
